import type { Property } from "@shared/schema";

export type MonthColumn = { label: string; key: string };

export function getMonthColumns(monthCount: number): MonthColumn[] {
  const cols: MonthColumn[] = [];
  const now = new Date();
  for (let i = 0; i < monthCount; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const label = d.toLocaleDateString("en-US", { month: "short", year: "numeric" });
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    cols.push({ label, key });
  }
  return cols;
}

// Task #2208 — Build P&L month columns from an arbitrary YYYY-MM-DD window
// (inclusive). Returns months in newest-first order to match the original
// trailing-N-months layout. Falls back to last 12 trailing months when the
// window is empty.
export function getMonthColumnsForRange(from: string, to: string): MonthColumn[] {
  if (!from || !to) return getMonthColumns(12);
  const start = new Date(`${from}T00:00:00`);
  const end = new Date(`${to}T00:00:00`);
  if (isNaN(start.getTime()) || isNaN(end.getTime()) || start > end) return getMonthColumns(12);
  const cols: MonthColumn[] = [];
  let y = start.getFullYear();
  let m = start.getMonth();
  const endY = end.getFullYear();
  const endM = end.getMonth();
  // Cap at 60 months to avoid a runaway custom range producing thousands of cols.
  let safety = 60;
  while ((y < endY || (y === endY && m <= endM)) && safety-- > 0) {
    const d = new Date(y, m, 1);
    cols.push({
      label: d.toLocaleDateString("en-US", { month: "short", year: "numeric" }),
      key: `${y}-${String(m + 1).padStart(2, "0")}`,
    });
    m++;
    if (m > 11) { m = 0; y++; }
  }
  cols.reverse();
  return cols;
}

export function sortPropertiesParentFirst(props: Property[]): Property[] {
  const sorted: Property[] = [];
  const parents = props.filter((p) => !p.parentPropertyId);
  for (const parent of parents) {
    sorted.push(parent);
    const children = props.filter((p) => p.parentPropertyId === parent.id);
    sorted.push(...children);
  }
  const orphans = props.filter((p) => p.parentPropertyId && !parents.some((par) => par.id === p.parentPropertyId));
  sorted.push(...orphans);
  return sorted;
}
